/**
 * SOLID Review
 * S — Strong. One concern: how many lives each seat at the table has left — full lives for a new game, the
 *     server's count, a life lost to a lift — and the count an unlisted seat reads as.
 * O — Good. A new way the count changes is one more method answering a new value.
 * L — N/A — no inheritance hierarchy (private constructor).
 * I — Strong. Three factories, four questions, the record.
 * D — Strong. The Domain's starting count and the active round's schema type.
 */
import { INITIAL_LIVES } from '../uml-components/primitives';
import type { IPayloadActiveRound } from '../ports/schemas/TableEvents';

/** The server's count: one entry per seat, in the order it lists them. */
type TCountedLives = IPayloadActiveRound['playerLives'];

/**
 * EVERY SEAT'S LIVES, AS A VALUE OBJECT (frontend-domain-oo P3, 2026-10-26; handbook §28.270). The table kept them as
 * a record that five decisions built by hand — `freshLives` in `decisions/table/tableDraft.ts` — and two read with
 * the same default, `?? INITIAL_LIVES`: the challenger rotation (`game/ChallengerSelection.ts`) and the cup-lift's
 * decrement (`decisions/table/serverEvents.ts`). Both are said once here.
 *
 * A seat the record does not list has the starting count: a missing entry is a bookkeeping gap, not an
 * elimination. Every change answers a new value; the record a value is built from is never written.
 */
export class TableLives {
  private constructor(private readonly lives: Readonly<Record<string, number>>) {}

  /** The table's record, as its snapshot holds it. */
  static of(lives: Readonly<Record<string, number>>): TableLives {
    return new TableLives(lives);
  }

  /** `initPlayerLives(ids)`: every listed seat at the starting count, and nobody else. */
  static full(playerIds: ReadonlyArray<string>): TableLives {
    return new TableLives(Object.fromEntries(playerIds.map((id) => [id, INITIAL_LIVES])));
  }

  /** The server's count, as it lists the seats. */
  static counted(entries: TCountedLives): TableLives {
    return new TableLives(Object.fromEntries(entries.map((e) => [e.playerId, e.lives])));
  }

  /** The lives `playerId` has left: the starting count for a seat the record does not list. */
  left(playerId: string): number {
    return this.lives[playerId] ?? INITIAL_LIVES;
  }

  /** Still in the game. */
  isAlive(playerId: string): boolean {
    return this.left(playerId) > 0;
  }

  /** The server counts some seat it lists differently — the record as held, an unlisted seat included. */
  differsFrom(entries: TCountedLives): boolean {
    return entries.some((e) => this.lives[e.playerId] !== e.lives);
  }

  /**
   * What the loser of a lift has left: the table's count when the server sends it (backend §28.191) — one fewer
   * than held, never below none, only for an older server's reveal, which carries none.
   */
  leftAfterLosing(loser: string, counted: number | null | undefined): number {
    return typeof counted === 'number' ? counted : Math.max(0, this.left(loser) - 1);
  }

  /** The same lives, with `playerId`'s count replaced (or added, last). */
  with(playerId: string, left: number): TableLives {
    const lives: Record<string, number> = { ...this.lives, [playerId]: left };
    return new TableLives(lives);
  }

  toRecord(): Record<string, number> {
    return { ...this.lives };
  }
}
